import { useTranslation } from 'react-i18next';
import { CHANGELOG_TYPES, changelogSchema, type Changelog } from '@quizdb/common';
import { Icon } from '../common/Icon';
import { inlineMarkdown } from './inlineMarkdown';
import changelogRecord from '../../../../../changelog.json';

/**
 * What changed in quizdb, release by release (QZ-D190). The changelog of record is bundled at
 * build time: it changes only at a release, and the web is rebuilt then. Only released entries
 * are shown, so the page says exactly what `CHANGELOG.md` says.
 */
export const ChangelogPage = ({ record = changelogRecord }: { record?: unknown }) => {
  const { t } = useTranslation();
  const parsed = changelogSchema.safeParse(record);
  const releases: Changelog['releases'] = parsed.success ? parsed.data.releases : [];

  return (
    <main className="qz-main">
      <div className="qz-container is-form">
        <div className="qz-page-header">
          <div>
            <h1>{t('changelog.heading')}</h1>
            <p className="qz-sub">{t('changelog.sub')}</p>
          </div>
        </div>
        {!parsed.success && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{t('changelog.error')}</div>
          </div>
        )}
        {parsed.success && releases.length === 0 && (
          <div className="qz-card">
            <div className="qz-empty">{t('changelog.empty')}</div>
          </div>
        )}
        <div className="qz-stack">
          {releases.map((release) => (
            <section key={release.date} className="qz-card">
              <div className="qz-card-header">
                <h3>{release.date}</h3>
              </div>
              <div className="qz-card-body">
                {CHANGELOG_TYPES.map((type) => {
                  const entries = release.entries.filter((entry) => entry.type === type);
                  if (entries.length === 0) return null;
                  return (
                    <div key={type}>
                      <h4 className="qz-section-title">{t(`changelog.type.${type}`)}</h4>
                      <ul>
                        {entries.map((entry, index) => (
                          <li key={index}>{inlineMarkdown(entry.text)}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ChangelogPage;
